import type { Product, Variant } from '@scayle/storefront-nuxt'
import type { FitMixInstance, FitMixParameters } from '~/types/fitmix'
import { useGlasses } from '~/composables/pdp/useGlasses'

export const globalVTOState = {
  isVTOOpen: ref(false),
  currentProduct: ref<Product | null>(null),
  currentVariant: ref<Variant | null>(null),
  vtoWidget: ref<FitMixInstance | null>(null),
  fitMixReady: ref(false),
  isInitializing: ref(false),
  isCleaningUp: ref(false),
  isScreenshotAllowed: ref(false),
  isScreenshotInProgress: ref(false),
}

const PLP_VTO_OVERLAY_CONTAINER_ID = 'plp-vto-overlay-container'

export function usePlpVTO() {
  const { $currentShop, $fittingBox } = useNuxtApp()
  const { name: productName } = useProductBaseInfo(
    globalVTOState.currentProduct,
  )
  const { productColor } = useGlasses(globalVTOState.currentProduct)

  const filename = computed(
    () => `${productName.value}-${productColor.value}.png`,
  )

  const handleSnapshot = (data: { dataUrl: string }) => {
    if (!globalVTOState.isScreenshotAllowed.value) {
      return
    }

    const link = document.createElement('a')
    link.href = data.dataUrl
    link.download = filename.value
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Reset screenshot states
    globalVTOState.isScreenshotAllowed.value = false
    globalVTOState.isScreenshotInProgress.value = false
  }

  const openVTO = async (product: Product, variant?: Variant) => {
    if (
      globalVTOState.isInitializing.value ||
      globalVTOState.isCleaningUp.value
    ) {
      return
    }

    if (globalVTOState.isVTOOpen.value) {
      await closeVTO()
    }

    globalVTOState.isInitializing.value = true
    globalVTOState.currentProduct.value = product
    globalVTOState.currentVariant.value = variant || null

    try {
      // Set VTO open first to render the overlay component
      globalVTOState.isVTOOpen.value = true

      // Wait for the component to render
      await nextTick()

      const container = document.getElementById(PLP_VTO_OVERLAY_CONTAINER_ID)
      if (!container) {
        console.error('[PlpVTO] VTO container not found')
        return
      }

      const vtoVariant = variant || product.variants?.[0]
      const vtoReference = vtoVariant ? getVirtualTryOnEan(vtoVariant) : null

      if (!vtoReference) {
        console.error('[PlpVTO] No VTO reference found')
        return
      }

      const vtoApiKey = $fittingBox?.vtoApiKey?.value
      if (!vtoApiKey) {
        console.error('[PlpVTO] VTO API key not found')
        return
      }

      if (!$fittingBox?.scriptReady?.value) {
        console.error('[PlpVTO] FitMix script not ready')
        return
      }

      const params: FitMixParameters = {
        apiKey: vtoApiKey,
        lang: getIsoLanguage(toValue($currentShop).locale),
        frame: vtoReference,
        uiConfiguration: {
          apiKeyIssueMessage: true,
          frameIssueMessage: true,
          liveCameraAccessDenied: true,
          liveDetectionFailure: true,
          liveLensesAnimations: true,
          liveLensesAnimationsActive: true,
          livePhotoButton: false,
          liveRelaunchDetectionButton: false,
          loadingIndicator: true,
          networkIssueMessage: true,
          photoLiveButton: false,
          photoRenderError: false,
          photoWelcomeScreen: true,
        },
        onSnapshot: handleSnapshot,
        onStopVto: () => {
          closeVTO()
        },
      }

      const widget = $fittingBox.createFitMixWidget(
        PLP_VTO_OVERLAY_CONTAINER_ID,
        params,
        (ready) => {
          globalVTOState.fitMixReady.value = ready
          if (ready) {
            widget?.startVto('live')
          }
        },
      )

      globalVTOState.vtoWidget.value = widget || null
    } catch (error) {
      console.error('[PlpVTO] Error opening VTO:', error)
      await closeVTO()
    } finally {
      globalVTOState.isInitializing.value = false
    }
  }

  const closeVTO = async () => {
    if (globalVTOState.isCleaningUp.value) {
      return
    }

    globalVTOState.isScreenshotAllowed.value = false
    globalVTOState.isCleaningUp.value = true

    try {
      if ($fittingBox) {
        $fittingBox.resetSession(PLP_VTO_OVERLAY_CONTAINER_ID)
        $fittingBox.stopVto(PLP_VTO_OVERLAY_CONTAINER_ID)
      }

      // Wait for the cleanup to complete
      await new Promise((resolve) => setTimeout(resolve, 50))
    } catch (error) {
      console.error('[PlpVTO] Error during cleanup:', error)
    } finally {
      globalVTOState.vtoWidget.value = null
      globalVTOState.isVTOOpen.value = false
      globalVTOState.currentProduct.value = null
      globalVTOState.currentVariant.value = null
      globalVTOState.fitMixReady.value = false
      globalVTOState.isInitializing.value = false
      globalVTOState.isCleaningUp.value = false
    }
  }

  return {
    isVTOOpen: readonly(globalVTOState.isVTOOpen),
    currentProduct: readonly(globalVTOState.currentProduct),
    currentVariant: readonly(globalVTOState.currentVariant),
    isInitializing: readonly(globalVTOState.isInitializing),
    isScreenshotAllowed: globalVTOState.isScreenshotAllowed,
    isScreenshotInProgress: globalVTOState.isScreenshotInProgress,
    fitMixReady: readonly(globalVTOState.fitMixReady),
    vtoWidget: globalVTOState.vtoWidget,
    openVTO,
    closeVTO,
  }
}
